import React, { useState, useEffect, useMemo } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GH_API = API_ROOT.endsWith("/api") ? `${API_ROOT}/guesthouse` : `${API_ROOT}/api/guesthouse`;

const PAYMENT_MODES = [
  { value: "P", label: "Pending" },
  { value: "C", label: "Cash" },
  { value: "Q", label: "Cheque" },
  { value: "O", label: "Online" },
  { value: "F", label: "Free Charge" }
];

function calcNAmount(f) {
  const amount = parseFloat(f.Amount)          || 0;
  const extra  = parseFloat(f.ExtraBedCharges) || 0;
  const penal  = parseFloat(f.PenalCharges)    || 0;
  const gst    = parseFloat(f.GST)             || 0;
  const base   = amount + extra + penal;
  return Math.round(base + (base * gst) / 100);
}

function fmtDate(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

const EMPTY_FORM = {
  VisitorName: "", VisitorMobile: "", VisitorEMail: "", Department: "",
  Address: "", StateID: "", CityID: "", VisitType: "O",
  RelationWithApplicant: "", ReceiptDate: "", PaymentMode: "P", UPIID: "",
  Amount: "", GST: "", ExtraBedCharges: "", PenalCharges: "",
  NAmount: "", JobID: "", GSTIN: "", UnitRent: "",
  PurposeOfVisit: "", RecieptNo: "", SCheckIn: "", SCheckOut: "",
  BookingID: "", BookingDate: "", GuestHouseID: "", RoomType: "",
  NoOfRooms: "", FromDate: "", ToDate: "", NoOfDays: ""
};

export default function GuesthouseStaffEditBooking({ bookingId, moduleId, onClose, onSaved }) {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);
  const empId = parseInt(localUser?.id, 10);

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [states,     setStates]     = useState([]);
  const [cities,     setCities]     = useState([]);
  const [logbook,    setLogbook]    = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);

  useEffect(() => {
    fetch(`${API_ROOT}/api/states`)
      .then(r => r.json())
      .then(d => setStates(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!bookingId) return;
    setLoading(true);
    setError("");
    setSuccess("");
    fetch(`${API_ROOT}/api/guesthouse/booking/staff-edit/${bookingId}?moduleId=${moduleId}`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.message || "Failed to load.");
        return d;
      })
      .then(data => {
        const b   = data.booking  || {};
        const inv = data.invoice  || {};
        const log = data.logbook  || {};
        const nn  = (v) => (v != null ? String(v) : "");
        const init = {
          VisitorName:           b.VisitorName           || "",
          VisitorMobile:         b.Mobile                || "",
          VisitorEMail:          b.Email                 || "",
          Department:            b.Department            || "",
          Address:               b.Address               || "",
          StateID:               nn(b.StateID),
          CityID:                nn(b.CityID),
          VisitType:             b.TypeOfVisit           || "O",
          RelationWithApplicant: b.Relationship          || "",
          ReceiptDate:           fmtDate(b.ReceiptDate),
          PaymentMode:           b.PaymentMode           || "P",
          UPIID:                 b.UPIID                 || "",
          Amount:                nn(b.Amount),
          GST:                   nn(b.GST),
          ExtraBedCharges:       nn(b.ExtraBedCharges),
          PenalCharges:          nn(b.PenalCharges),
          NAmount:               "",
          JobID:                 b.JobID                 || "",
          GSTIN:                 b.GSTIN                 || "",
          UnitRent:              nn(b.UnitRent),
          PurposeOfVisit:        b.PurposeOfVisit        || "",
          RecieptNo:             inv.InvoiceNumber       || "",
          SCheckIn:              "",
          SCheckOut:             "",
          BookingID:             nn(b.BookingID),
          BookingDate:           fmtDate(b.BookingDate),
          GuestHouseID:          nn(b.GuestHouseID),
          RoomType:              b.RoomType              || "",
          NoOfRooms:             nn(b.NoOfRooms),
          FromDate:              fmtDate(b.FromDate),
          ToDate:                fmtDate(b.ToDate),
          NoOfDays:              nn(b.NoOfDays)
        };
        init.NAmount = String(calcNAmount(init));
        setForm(init);
        setLogbook(log);
      })
      .catch(err => setError(err.message || "Failed to load booking data."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, moduleId]);

  useEffect(() => {
    if (!form.StateID) { setCities([]); return; }
    fetch(`${API_ROOT}/api/cities?stateId=${form.StateID}`)
      .then(r => r.json())
      .then(d => setCities(Array.isArray(d) ? d : []))
      .catch(() => setCities([]));
  }, [form.StateID]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === "StateID") next.CityID = "";
      if (name === "PaymentMode" && value === "F") {
        next.UnitRent = "0";
        next.Amount = "0";
        next.NAmount = String(calcNAmount({ ...next, Amount: "0" }));
      }
      if (["Amount", "ExtraBedCharges", "PenalCharges", "GST"].includes(name)) {
        next.NAmount = String(calcNAmount(next));
      }
      return next;
    });
  };

  const alreadyCheckedIn  = logbook ? logbook.CheckIn  !== null : false;
  const alreadyCheckedOut = logbook ? logbook.CheckOut !== null : false;

  // Mirrors calculateDays() in ChangeRequestbyStaff.php — fetches CheckIn time
  // and re-derives NoOfDays/Amount/NAmount from elapsed time x UnitRent.
  const onCheckOutSelect = async (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, SCheckOut: value }));
    if (value !== "O" || !form.BookingID) return;

    try {
      const res = await fetch(`${API_ROOT}/api/guesthouse/booking/checkin-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ moduleId, BookingID: form.BookingID })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch check-in info.");
      if (!data.CheckIn) {
        setError("Check-In not found.");
        return;
      }
      const checkInDate  = new Date(String(data.CheckIn).split(" ")[0]);
      const checkOutDate = new Date();
      const diffTime = checkOutDate - checkInDate;
      let diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) diffDays = 1;

      const unitRent = parseFloat(form.UnitRent) || 0;
      const amount = diffDays * unitRent;
      const extraBed = parseFloat(form.ExtraBedCharges) || 0;
      const penal    = parseFloat(form.PenalCharges) || 0;
      const netAmount = amount + extraBed + penal;

      setForm(prev => ({
        ...prev,
        NoOfDays: String(diffDays),
        Amount: amount.toFixed(2),
        NAmount: netAmount.toFixed(2)
      }));
    } catch (err) {
      setError(err.message || "Failed to calculate check-out amount.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const payload = {
        moduleId,
        empId,
        VisitorName: form.VisitorName,
        VisitorMobile: form.VisitorMobile,
        VisitorEMail: form.VisitorEMail,
        Department: form.Department,
        Address: form.Address,
        CityID: form.CityID,
        StateID: form.StateID,
        VisitType: form.VisitType,
        RelationWithApplicant: form.RelationWithApplicant,
        ReceiptDate: form.ReceiptDate,
        PaymentMode: form.PaymentMode,
        UPIID: form.UPIID,
        Amount: form.Amount,
        GST: form.GST,
        NAmount: form.NAmount,
        ExtraBedCharges: form.ExtraBedCharges,
        PenalCharges: form.PenalCharges,
        JobID: form.JobID,
        GSTIN: form.GSTIN,
        UnitRent: form.UnitRent,
        RecieptNo: form.RecieptNo,
        SCheckIn:  alreadyCheckedIn                       ? "" : form.SCheckIn,
        SCheckOut: !alreadyCheckedIn || alreadyCheckedOut ? "" : form.SCheckOut
      };

      const res  = await fetch(
        `${API_ROOT}/api/guesthouse/booking/staff-edit/${bookingId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update.");
      setSuccess(data.message || "Booking Request is successfully updated.");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message || "Failed to update.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    if (!form.BookingID) { alert("Booking ID not found!"); return; }
    const q = new URLSearchParams({ moduleId: String(moduleId) });
    window.open(`${GH_API}/booking/print/${encodeURIComponent(form.BookingID)}?${q.toString()}`, "_blank", "noopener,noreferrer");
  };

  const roomTypeLabel = form.RoomType === "R" ? "Room" : form.RoomType === "S" ? "Suite" : (form.RoomType || "");

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content" style={{ backgroundColor: "#e8e8e8" }}>

          <div className="modal-header py-2">
            <h5 className="modal-title fw-bold">Change Booking Request (By Staff)</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading  && <div className="alert alert-info mb-2">Loading...</div>}
            {error    && <div className="alert alert-danger mb-2">{error}</div>}
            {success  && <div className="alert alert-success mb-2">{success}</div>}

            {!loading && (
              <form onSubmit={onSubmit}>
                <div className="row g-2">

                  {/* ── Row 1: Visitor ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Name of Visitor</label>
                    <input className="form-control form-control-sm" name="VisitorName" value={form.VisitorName} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Mobile</label>
                    <input className="form-control form-control-sm" name="VisitorMobile" value={form.VisitorMobile} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Department</label>
                    <input className="form-control form-control-sm" name="Department" value={form.Department} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Email (Optional)</label>
                    <input className="form-control form-control-sm" name="VisitorEMail" value={form.VisitorEMail} onChange={onChange} />
                  </div>

                  {/* ── Row 2: Address / State / City / Relationship ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Permanent Address</label>
                    <input className="form-control form-control-sm" name="Address" value={form.Address} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">State</label>
                    <select className="form-select form-select-sm" name="StateID" value={form.StateID} onChange={onChange}>
                      <option value="">-- Select State --</option>
                      {states.map(s => <option key={s.StateID} value={s.StateID}>{s.State}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">City</label>
                    <select className="form-select form-select-sm" name="CityID" value={form.CityID} onChange={onChange}>
                      <option value="">-- Select City --</option>
                      {cities.map(c => <option key={c.CityID} value={c.CityID}>{c.City}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Relationship</label>
                    <input className="form-control form-control-sm" name="RelationWithApplicant" value={form.RelationWithApplicant} onChange={onChange} />
                  </div>

                  {/* ── Row 3: GH info (read-only) / Visit type ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Guest House</label>
                    <input className="form-control form-control-sm" value={form.GuestHouseID} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Room Type</label>
                    <input className="form-control form-control-sm" value={roomTypeLabel} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Type of Visit</label>
                    <select className="form-select form-select-sm" name="VisitType" value={form.VisitType} onChange={onChange}>
                      <option value="O">Official</option>
                      <option value="P">Personal</option>
                    </select>
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">No. of Rooms</label>
                    <input className="form-control form-control-sm" value={form.NoOfRooms} readOnly disabled />
                  </div>

                  {/* ── Row 4: Purpose (read-only) / Booking dates (read-only) ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Purpose of Visit</label>
                    <input className="form-control form-control-sm" value={form.PurposeOfVisit} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Check-In (From Date)</label>
                    <input className="form-control form-control-sm" type="date" value={form.FromDate} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Check-Out (To Date)</label>
                    <input className="form-control form-control-sm" type="date" value={form.ToDate} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">No. of Days</label>
                    <input className="form-control form-control-sm" value={form.NoOfDays} readOnly disabled />
                  </div>

                  {/* ── Row 5: Invoice / Payment ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Invoice No.</label>
                    <input className="form-control form-control-sm" value={form.RecieptNo} readOnly />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Receipt Date</label>
                    <input className="form-control form-control-sm" type="date" name="ReceiptDate" value={form.ReceiptDate} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Payment Mode</label>
                    <select className="form-select form-select-sm" name="PaymentMode" value={form.PaymentMode} onChange={onChange}>
                      {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Transaction ID</label>
                    <input className="form-control form-control-sm" name="UPIID" value={form.UPIID} onChange={onChange} placeholder="Transaction ID" />
                  </div>

                  {/* ── Row 6: Amounts ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Amount</label>
                    <input className="form-control form-control-sm" name="Amount" value={form.Amount} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Extra Bed Charges</label>
                    <input className="form-control form-control-sm" name="ExtraBedCharges" value={form.ExtraBedCharges} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Misc Charges</label>
                    <input className="form-control form-control-sm" name="PenalCharges" value={form.PenalCharges} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Net Amount</label>
                    <input className="form-control form-control-sm" value={form.NAmount} readOnly />
                  </div>

                  {/* ── Row 7: Actual Check-In/Out (status dropdowns) ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">
                      Check-In (Actual)
                      {alreadyCheckedIn && <span className="ms-1 text-success">(Done)</span>}
                    </label>
                    {alreadyCheckedIn
                      ? <input className="form-control form-control-sm" value={fmtDate(logbook.CheckIn)} readOnly disabled />
                      : (
                        <select className="form-select form-select-sm" name="SCheckIn" value={form.SCheckIn} onChange={onChange}>
                          <option value="" disabled>Status</option>
                          <option value="I">Check In</option>
                        </select>
                      )
                    }
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">
                      Check-Out (Actual)
                      {alreadyCheckedOut && <span className="ms-1 text-success">(Done)</span>}
                    </label>
                    {alreadyCheckedOut
                      ? <input className="form-control form-control-sm" value={fmtDate(logbook.CheckOut)} readOnly disabled />
                      : (
                        <select className="form-select form-select-sm" name="SCheckOut" value={form.SCheckOut} onChange={onCheckOutSelect} disabled={!alreadyCheckedIn}>
                          <option value="" disabled>Status</option>
                          <option value="O">Check Out</option>
                        </select>
                      )
                    }
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Job ID</label>
                    <input className="form-control form-control-sm" name="JobID" value={form.JobID} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">GSTIN</label>
                    <input className="form-control form-control-sm" name="GSTIN" value={form.GSTIN} onChange={onChange} />
                  </div>

                  {/* ── Row 8: GST% / Unit Rent / Booking ID / Booking Date ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">GST %</label>
                    <input className="form-control form-control-sm" name="GST" value={form.GST} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Unit Rent</label>
                    <input className="form-control form-control-sm" name="UnitRent" value={form.UnitRent} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Booking ID</label>
                    <input className="form-control form-control-sm" value={form.BookingID} readOnly />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Booking Date</label>
                    <input className="form-control form-control-sm" type="date" value={form.BookingDate} readOnly />
                  </div>

                </div>

                <div className="mt-3 d-flex gap-2">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                    {submitting ? "Saving…" : "Save"}
                  </button>
                  <button type="button" className="btn btn-outline-dark btn-sm" onClick={handlePrint}>
                    Print
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                    Close
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
